/**
 * «Свои данные как источник»: таблица продавца (Google-таблица по ссылке или
 * файл Excel/CSV) становится колонками FrogStat.
 *
 * Этот модуль — без базы и без сети: типы, заготовки колонок («Мой склад»,
 * «Заказано у поставщика»…), разбор CSV, нормализация ключей, угадывание
 * столбцов и значения ячеек. Его читают и сервер (lib/external-sources-store.ts,
 * API), и клиент (окно подключения, редактор колонок, ячейки таблицы).
 *
 * Как устроено:
 *   источник  — одна подключённая таблица магазина: откуда (ссылка/файл),
 *               какой столбец — ключ и что он значит (артикул, артикул модели,
 *               штрихкод, SKU), какие столбцы стали колонками;
 *   колонка   — столбец источника с именем, типом (число/текст/дата/ссылка),
 *               правилом итога у модели и, если она из заготовки, ролью;
 *   ключ колонки в таблицах — `ext:<источник>:<тип><id>` (EXTERNAL_COLUMN_PREFIX):
 *               тип зашит в ключ, чтобы сортировка и фильтр знали «число это
 *               или текст» без обращения к списку источников.
 *
 * Значения хранятся по нормализованному ключу строки (lib/external-sources-store),
 * а к товарам привязываются при чтении: артикул/штрихкод/SKU из каталога
 * магазина, артикул модели — по group_article и артикулу без размера.
 */

export const EXTERNAL_COLUMN_PREFIX = "ext:";

export type ExternalSourceKind = "google" | "file";

/** Что лежит в столбце-ключе таблицы пользователя. */
export const EXTERNAL_KEY_KINDS = ["offer", "model", "barcode", "sku"] as const;
export type ExternalKeyKind = (typeof EXTERNAL_KEY_KINDS)[number];

export const EXTERNAL_KEY_KIND_LABELS: Record<ExternalKeyKind, string> = {
  offer: "Артикул",
  model: "Артикул модели",
  barcode: "Штрихкод",
  sku: "SKU Ozon",
};

export const EXTERNAL_KEY_KIND_HINTS: Record<ExternalKeyKind, string> = {
  offer: "Совпадает с артикулом товара в Ozon; регистр и пробелы не важны.",
  model: "Одна строка на модель: значение подставляется всем её размерам.",
  barcode: "Штрихкод размера, как в карточке Ozon.",
  sku: "Номер SKU из кабинета Ozon.",
};

export const EXTERNAL_COLUMN_TYPES = ["number", "text", "date", "link"] as const;
export type ExternalColumnType = (typeof EXTERNAL_COLUMN_TYPES)[number];

export const EXTERNAL_COLUMN_TYPE_LABELS: Record<ExternalColumnType, string> = {
  number: "Число",
  text: "Текст",
  date: "Дата",
  link: "Ссылка",
};

/** Итог у модели (и в строке «Итого») для числовой колонки. */
export const EXTERNAL_AGGREGATES = ["sum", "avg", "max", "min", "first", "none"] as const;
export type ExternalAggregate = (typeof EXTERNAL_AGGREGATES)[number];

export const EXTERNAL_AGGREGATE_LABELS: Record<ExternalAggregate, string> = {
  sum: "сумма",
  avg: "среднее",
  max: "наибольшее",
  min: "наименьшее",
  first: "как у размеров",
  none: "не складывать",
};

/** Единицы у числовых колонок — подпись в шапке и формат ячейки. */
export type ExternalUnit = "" | "₽" | "шт" | "дн" | "г" | "%";

export type ExternalColumn = {
  /** Короткий id внутри источника; первая буква — тип (n/t/d/l), см. externalColumnKey. */
  id: string;
  /** Заголовок столбца в таблице пользователя, как прочитан. */
  header: string;
  label: string;
  type: ExternalColumnType;
  aggregate: ExternalAggregate;
  unit: ExternalUnit;
  /** Заготовка, из которой колонка сделана (EXTERNAL_PRESETS), или null у «своей колонки». */
  role: string | null;
};

export type ExternalSource = {
  id: string;
  storeId: string;
  name: string;
  kind: ExternalSourceKind;
  /** Ссылка на Google-таблицу (kind google) — в том виде, как её вставили. */
  url: string | null;
  /** Имя загруженного файла (kind file). */
  fileName: string | null;
  keyHeader: string;
  keyKind: ExternalKeyKind;
  /** Первая строка таблицы — заголовки (false — данные, столбцы зовутся «Столбец A/B…»). */
  headerRow: boolean;
  columns: ExternalColumn[];
  createdAt: string;
  updatedAt: string;
  /** Последнее чтение таблицы (с записью значений). */
  lastSyncAt: string | null;
  /** Последняя проверка «не изменилась ли» (Google, поминутно). */
  lastCheckedAt: string | null;
  /** Когда данные в таблице в последний раз менялись — по этому страница перечитывает значения. */
  dataChangedAt: string | null;
  lastStatus: "ok" | "error" | null;
  lastError: string | null;
  rowsTotal: number;
  rowsMatched: number;
  /** Ключи строк, которым не нашлось товара (первые EXTERNAL_UNMATCHED_SAMPLE). */
  unmatchedSample: string[];
};

export const EXTERNAL_UNMATCHED_SAMPLE = 40;
export const EXTERNAL_SOURCE_NAME_MAX = 80;
export const EXTERNAL_COLUMN_LABEL_MAX = 60;
export const EXTERNAL_HEADER_MAX = 120;
/** Больше строк из одной таблицы не читаем: это справочник по товарам, а не журнал. */
export const EXTERNAL_MAX_ROWS = 20_000;
export const EXTERNAL_MAX_COLUMNS = 60;
/** Предел размера файла и ответа Google, байт. */
export const EXTERNAL_MAX_BYTES = 8 * 1024 * 1024;
/** Сколько колонок можно взять из одного источника. */
export const EXTERNAL_MAX_SOURCE_COLUMNS = 20;

/**
 * Заготовки: клик по «Мой склад» в редакторе колонок открывает то же окно
 * подключения, но имя, тип, единица и итог у модели уже заданы; пользователь
 * показывает только столбец с артикулом и столбец со значением.
 * `hints` — по ним угадывается столбец со значением в таблице пользователя.
 * Роль остаётся у колонки: планирование и правила потом возьмут её по роли.
 */
export type ExternalPreset = {
  id: string;
  label: string;
  type: ExternalColumnType;
  aggregate: ExternalAggregate;
  unit: ExternalUnit;
  description: string;
  hints: readonly RegExp[];
  /** Заготовка «Своя колонка»: несколько столбцов, имя и тип задаёт пользователь. */
  custom?: boolean;
};

export const EXTERNAL_PRESETS: readonly ExternalPreset[] = [
  { id: "my_stock", label: "Мой склад", type: "number", aggregate: "sum", unit: "шт",
    description: "Остаток на вашем складе по размерам — из вашей таблицы учёта.",
    hints: [/остат/i, /склад/i, /налич/i, /кол-?во/i, /\bqty\b/i, /stock/i] },
  { id: "fulfillment", label: "Фулфилмент", type: "number", aggregate: "sum", unit: "шт",
    description: "Остаток на складе фулфилмент-партнёра — из его отчёта или вашей таблицы.",
    hints: [/фулф/i, /\bff\b/i, /остат/i, /склад/i] },
  { id: "supplier_ordered", label: "Заказано у поставщика", type: "number", aggregate: "sum", unit: "шт",
    description: "Сколько штук заказано у фабрики и ещё не приехало.",
    hints: [/заказан/i, /в пути/i, /ordered/i, /фабрик/i, /поставщик/i] },
  { id: "site_price", label: "Цена на сайте", type: "number", aggregate: "avg", unit: "₽",
    description: "Цена в вашей рознице или на своём сайте — рядом с ценой Ozon.",
    hints: [/розн/i, /сайт/i, /retail/i, /цена/i, /price/i] },
  { id: "link", label: "Ссылка", type: "link", aggregate: "first", unit: "",
    description: "Ссылка на папку с фото, страницу поставщика, техкарту — открывается из строки.",
    hints: [/ссылк/i, /\burl\b/i, /link/i, /http/i] },
  { id: "manager_note", label: "Заметка менеджера", type: "text", aggregate: "first", unit: "",
    description: "Комментарий из вашей таблицы — виден в строке товара.",
    hints: [/заметк/i, /коммент/i, /примеч/i, /note/i, /comment/i] },
  { id: "custom", label: "Своя колонка из таблицы", type: "number", aggregate: "sum", unit: "",
    description: "Любые столбцы вашей таблицы: имя и тип задаёте сами.",
    hints: [], custom: true },
];

export function getExternalPreset(id: string | null | undefined): ExternalPreset | null {
  if (!id) return null;
  return EXTERNAL_PRESETS.find(preset => preset.id === id) ?? null;
}

// ── Ключи колонок ────────────────────────────────────────────────────────────

const TYPE_LETTER: Record<ExternalColumnType, string> = { number: "n", text: "t", date: "d", link: "l" };
const LETTER_TYPE: Record<string, ExternalColumnType> = { n: "number", t: "text", d: "date", l: "link" };

/** Id колонки: буква типа + 8 знаков — тип читается из ключа. */
export function makeExternalColumnId(type: ExternalColumnType, random: () => string = randomId): string {
  return `${TYPE_LETTER[type]}${random()}`;
}

function randomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID().replace(/-/g, "").slice(0, 8);
  return Math.random().toString(36).slice(2, 10).padEnd(8, "0");
}

const COLUMN_ID_RE = /^[ntdl][a-zA-Z0-9]{4,32}$/;
const UNITS: readonly ExternalUnit[] = ["", "₽", "шт", "дн", "г", "%"];

/**
 * Колонки из запроса клиента — только допустимые значения: id по формату и с
 * буквой своего типа, имя не длиннее лимита, тип/итог/единица из списков,
 * роль — известная заготовка или null. Повторы id отбрасываются.
 */
export function sanitizeExternalColumns(raw: unknown): ExternalColumn[] {
  if (!Array.isArray(raw)) return [];
  const out: ExternalColumn[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const value = item as Record<string, unknown>;
    const type = EXTERNAL_COLUMN_TYPES.find(candidate => candidate === value.type);
    const id = typeof value.id === "string" ? value.id : "";
    if (!type || !COLUMN_ID_RE.test(id) || id[0] !== TYPE_LETTER[type] || seen.has(id)) continue;
    const header = typeof value.header === "string" ? value.header.trim().slice(0, EXTERNAL_HEADER_MAX) : "";
    const label = typeof value.label === "string" ? value.label.replace(/\s+/g, " ").trim().slice(0, EXTERNAL_COLUMN_LABEL_MAX) : "";
    if (!header || !label) continue;
    const aggregate = EXTERNAL_AGGREGATES.find(candidate => candidate === value.aggregate) ?? (type === "number" ? "sum" : "first");
    const unit = type === "number" ? (UNITS.find(candidate => candidate === value.unit) ?? "") : "";
    const role = typeof value.role === "string" && getExternalPreset(value.role) && !getExternalPreset(value.role)?.custom ? value.role : null;
    seen.add(id);
    out.push({ id, header, label, type, aggregate, unit, role });
  }
  return out;
}

export function externalColumnKey(sourceId: string, columnId: string): string {
  return `${EXTERNAL_COLUMN_PREFIX}${sourceId}:${columnId}`;
}

export type ParsedExternalKey = { sourceId: string; columnId: string; type: ExternalColumnType };

const EXTERNAL_KEY_RE = /^ext:([a-zA-Z0-9_-]{4,64}):([ntdl][a-zA-Z0-9]{4,32})$/;

export function parseExternalColumnKey(key: string): ParsedExternalKey | null {
  const match = EXTERNAL_KEY_RE.exec(key);
  if (!match) return null;
  return { sourceId: match[1], columnId: match[2], type: LETTER_TYPE[match[2][0]] };
}

export function isExternalColumnKey(key: string): boolean {
  return key.startsWith(EXTERNAL_COLUMN_PREFIX) && EXTERNAL_KEY_RE.test(key);
}

// ── Известные источники на клиенте: подписи и описания по ключу ─────────────

/**
 * Подписи колонок везде берутся по ключу (getColLabel), а имена колонок
 * источника живут в базе. Клиент, загрузив список источников, кладёт его
 * сюда; на сервере реестр пуст, и ключ подписывается «Колонка из таблицы».
 * Так же устроены виды услуг у денег (accrualTypeLabel) — справочник,
 * который подгружается отдельно от кода.
 */
const KNOWN_SOURCES = new Map<string, ExternalSource>();

export function setKnownExternalSources(sources: readonly ExternalSource[]): void {
  KNOWN_SOURCES.clear();
  for (const source of sources) KNOWN_SOURCES.set(source.id, source);
}

export function getKnownExternalSource(sourceId: string): ExternalSource | null {
  return KNOWN_SOURCES.get(sourceId) ?? null;
}

export type ExternalColumnDef = { source: ExternalSource; column: ExternalColumn; type: ExternalColumnType };

export function getExternalColumnDef(key: string): ExternalColumnDef | null {
  const parsed = parseExternalColumnKey(key);
  if (!parsed) return null;
  const source = KNOWN_SOURCES.get(parsed.sourceId);
  const column = source?.columns.find(item => item.id === parsed.columnId);
  if (!source || !column) return null;
  return { source, column, type: column.type };
}

/** Подпись в шапке и редакторе: имя колонки; у числа с единицей — «Мой склад, шт». */
export function externalColumnLabel(key: string): string | null {
  const def = getExternalColumnDef(key);
  if (!def) return isExternalColumnKey(key) ? "Колонка из таблицы" : null;
  const unit = def.column.type === "number" && def.column.unit ? `, ${def.column.unit}` : "";
  return `${def.column.label}${unit}`;
}

export function externalColumnDescription(key: string): string | null {
  const def = getExternalColumnDef(key);
  if (!def) return null;
  const preset = getExternalPreset(def.column.role);
  const where = def.source.kind === "google" ? "Google-таблицы" : `файла «${def.source.fileName ?? def.source.name}»`;
  const aggregate = def.column.type === "number" ? ` У модели — ${EXTERNAL_AGGREGATE_LABELS[def.column.aggregate]}.` : "";
  return `${preset ? `${preset.description} ` : ""}Столбец «${def.column.header}» из ${where} «${def.source.name}», привязка по полю «${EXTERNAL_KEY_KIND_LABELS[def.source.keyKind].toLowerCase()}».${aggregate}\nОбновление: ${externalRefreshLabel(def.source)}.`;
}

// ── Нормализация ключей ──────────────────────────────────────────────────────

/** Ключ строки таблицы пользователя в том виде, в каком его сравнивают с товаром. */
export function normalizeExternalKey(raw: unknown, kind: ExternalKeyKind): string {
  const text = String(raw ?? "").replace(/^\s*'/, "").trim();
  if (!text) return "";
  if (kind === "barcode" || kind === "sku") {
    // Штрихкоды и SKU — числа; таблицы пишут их с пробелами, апострофом или как 4.6E+12.
    if (/e\+?\d+$/i.test(text.replace(/\s/g, ""))) {
      const asNumber = Number(text.replace(/\s/g, "").replace(",", "."));
      if (Number.isFinite(asNumber)) return BigInt(Math.round(asNumber)).toString();
    }
    return text.replace(/\D/g, "");
  }
  return text.replace(/\s+/g, " ").toUpperCase();
}

const SIZE_TAIL = /^(\d{1,3}(?:[.,]\d)?|\d{2,3}-\d{2,3}|x{0,3}[sml]|xs|xxl|xxxl|2xl|3xl|4xl|один размер|one size|os|u|uni)$/i;

/**
 * По каким ключам товар считается «моделью»: артикул модели из каталога
 * (group_article) и артикул без размера — Ozon-продавцы пишут размер после
 * «/» («1230121черный_бархат/46») или через «-» («SYN-0002-39»).
 */
export function modelKeysOfProduct(offerId: string, groupArticle: string | null | undefined): string[] {
  const keys = new Set<string>();
  const offer = normalizeExternalKey(offerId, "model");
  if (groupArticle) keys.add(normalizeExternalKey(groupArticle, "model"));
  const slash = offer.lastIndexOf("/");
  if (slash > 0) keys.add(offer.slice(0, slash).trim());
  const dash = offer.lastIndexOf("-");
  if (dash > 0 && SIZE_TAIL.test(offer.slice(dash + 1).trim())) keys.add(offer.slice(0, dash).trim());
  const space = offer.lastIndexOf(" ");
  if (space > 0 && SIZE_TAIL.test(offer.slice(space + 1))) keys.add(offer.slice(0, space).trim());
  // Товар без размеров: его артикул и есть артикул модели.
  keys.add(offer);
  keys.delete("");
  return [...keys];
}

// ── Значения ячеек ───────────────────────────────────────────────────────────

export type ExternalCellValue = number | string | null;

/** «1 234,50» → 1234.5; «12%» → 12; пусто, прочерк, текст → null. */
export function parseExternalNumber(raw: unknown): number | null {
  if (raw == null) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw === "boolean") return raw ? 1 : 0;
  let text = String(raw).trim().replace(/[₽$€%]/g, "").replace(/ /g, " ");
  if (!text || /^[-—–]+$/.test(text)) return null;
  text = text.replace(/\s/g, "");
  // Обе точки и запятая: последняя — десятичный разделитель, остальные — разряды.
  const lastComma = text.lastIndexOf(",");
  const lastDot = text.lastIndexOf(".");
  if (lastComma !== -1 && lastDot !== -1) {
    text = lastComma > lastDot ? text.replace(/\./g, "").replace(",", ".") : text.replace(/,/g, "");
  } else if (lastComma !== -1) {
    text = text.replace(",", ".");
  }
  if (!/^[-+]?\d*\.?\d+(e[-+]?\d+)?$/i.test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);

/** Дата из ячейки → «ГГГГ-ММ-ДД»: объект Date, «21.09.2026», «2026-09-21», «21/09/26», число Excel. */
export function parseExternalDate(raw: unknown): string | null {
  if (raw == null || raw === "") return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw.toISOString().slice(0, 10);
  if (typeof raw === "number") {
    if (raw > 20_000 && raw < 80_000) return new Date(EXCEL_EPOCH_MS + Math.round(raw) * 86_400_000).toISOString().slice(0, 10);
    return null;
  }
  const text = String(raw).trim();
  let match = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(text);
  if (match) return isoDate(Number(match[1]), Number(match[2]), Number(match[3]));
  match = /^(\d{1,2})[./](\d{1,2})[./](\d{2,4})$/.exec(text);
  if (match) {
    const year = match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]);
    return isoDate(year, Number(match[2]), Number(match[1]));
  }
  return null;
}

function isoDate(year: number, month: number, day: number): string | null {
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1990 || year > 2100) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCMonth() !== month - 1) return null;
  return date.toISOString().slice(0, 10);
}

export function parseExternalLink(raw: unknown): string | null {
  const text = String(raw ?? "").trim();
  if (!text) return null;
  const withScheme = /^https?:\/\//i.test(text) ? text : /^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(text) ? `https://${text}` : null;
  if (!withScheme || withScheme.length > 2000) return null;
  try {
    const url = new URL(withScheme);
    return url.protocol === "http:" || url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

/** Значение ячейки по типу колонки; ничего разумного — null. */
export function parseExternalCell(raw: unknown, type: ExternalColumnType): ExternalCellValue {
  if (type === "number") return parseExternalNumber(raw);
  if (type === "date") return parseExternalDate(raw);
  if (type === "link") return parseExternalLink(raw);
  const text = cellText(raw).trim();
  return text ? text.slice(0, 500) : null;
}

/** Текст ячейки как её видит человек: даты — «ГГГГ-ММ-ДД», числа — без хвостов. */
export function cellText(raw: unknown): string {
  if (raw == null) return "";
  if (raw instanceof Date) return parseExternalDate(raw) ?? "";
  if (typeof raw === "number") return Number.isInteger(raw) ? String(raw) : String(Math.round(raw * 1e6) / 1e6);
  return String(raw);
}

// ── Угадывание ───────────────────────────────────────────────────────────────

export type ExternalSheet = { headers: string[]; rows: string[][] };

/** По содержимому столбца: сколько ячеек похожи на ключ этого вида. */
export function keyLikeShare(values: readonly string[], kind: ExternalKeyKind): number {
  const filled = values.map(value => value.trim()).filter(Boolean);
  if (!filled.length) return 0;
  const like = filled.filter(value => {
    if (kind === "barcode") return /^\d{8,14}$/.test(value.replace(/\s/g, ""));
    if (kind === "sku") return /^\d{6,12}$/.test(value.replace(/\s/g, ""));
    return /[a-zа-яё]/i.test(value) || /\d/.test(value);
  });
  return like.length / filled.length;
}

const KEY_HEADER_HINTS: Record<ExternalKeyKind, readonly RegExp[]> = {
  offer: [/артикул/i, /\bsku\b/i, /код товара/i, /offer/i, /\bart\b/i, /арт\./i],
  model: [/модел/i, /артикул модели/i, /model/i],
  barcode: [/штрих/i, /\bean\b/i, /barcode/i, /шк/i],
  sku: [/sku ozon/i, /ozon sku/i, /\bsku\b/i],
};

/** Какой столбец похож на столбец с ключом: по заголовку, потом по содержимому. */
export function guessKeyHeader(sheet: ExternalSheet, kind: ExternalKeyKind = "offer"): string | null {
  const byHeader = sheet.headers.find(header => KEY_HEADER_HINTS[kind].some(hint => hint.test(header)));
  if (byHeader) return byHeader;
  // Ключ — заполнен и почти без повторов: доля похожих на ключ × доля уникальных.
  let bestHeader: string | null = null;
  let bestScore = 0;
  sheet.headers.forEach((header, index) => {
    const values = sheet.rows.map(row => row[index] ?? "");
    const distinct = new Set(values.map(value => value.trim()).filter(Boolean)).size;
    const filled = values.filter(value => value.trim()).length;
    if (!filled) return;
    const score = keyLikeShare(values, kind) * (distinct / filled);
    if (score > bestScore) { bestScore = score; bestHeader = header; }
  });
  return bestScore > 0.5 ? bestHeader : null;
}

/** Столбец под заготовку: по подсказкам заголовка, не совпадающий с ключом. */
export function guessPresetHeader(sheet: ExternalSheet, preset: ExternalPreset, keyHeader: string | null): string | null {
  const candidates = sheet.headers.filter(header => header !== keyHeader);
  for (const hint of preset.hints) {
    const found = candidates.find(header => hint.test(header));
    if (found) return found;
  }
  if (preset.type === "number") {
    // Первый числовой столбец, если по заголовку не угадали, — но не столбец
    // штрихкодов/SKU (длинные числа — это идентификаторы, а не количество).
    const index = sheet.headers.findIndex((header, at) => {
      if (header === keyHeader) return false;
      const values = sheet.rows.map(row => row[at] ?? "");
      return numericShare(values) > 0.8 && keyLikeShare(values, "barcode") < 0.5 && keyLikeShare(values, "sku") < 0.5;
    });
    return index >= 0 ? sheet.headers[index] : null;
  }
  return null;
}

export function numericShare(values: readonly string[]): number {
  const filled = values.map(value => value.trim()).filter(Boolean);
  if (!filled.length) return 0;
  return filled.filter(value => parseExternalNumber(value) !== null).length / filled.length;
}

/** Тип столбца по содержимому: числа, даты, ссылки, иначе текст. */
export function guessColumnType(values: readonly string[]): ExternalColumnType {
  const filled = values.map(value => value.trim()).filter(Boolean);
  if (!filled.length) return "text";
  const share = (test: (value: string) => boolean) => filled.filter(test).length / filled.length;
  if (share(value => parseExternalLink(value) !== null && /^(https?:\/\/|www\.)/i.test(value)) > 0.8) return "link";
  if (share(value => parseExternalDate(value) !== null) > 0.8) return "date";
  if (share(value => parseExternalNumber(value) !== null) > 0.8) return "number";
  return "text";
}

// ── CSV ──────────────────────────────────────────────────────────────────────

/** Разбор CSV: кавычки, переносы внутри кавычек, разделитель «,» или «;» по первой строке. */
export function parseCsv(text: string, maxRows = EXTERNAL_MAX_ROWS + 1): string[][] {
  const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const firstLine = source.slice(0, source.indexOf("\n") === -1 ? source.length : source.indexOf("\n"));
  const delimiter = countOutsideQuotes(firstLine, ";") > countOutsideQuotes(firstLine, ",") ? ";" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < source.length; i += 1) {
    const char = source[i];
    if (quoted) {
      if (char === "\"") {
        if (source[i + 1] === "\"") { field += "\""; i += 1; } else quoted = false;
      } else field += char;
      continue;
    }
    if (char === "\"") { quoted = true; continue; }
    if (char === delimiter) { row.push(field); field = ""; continue; }
    if (char === "\n" || char === "\r") {
      if (char === "\r" && source[i + 1] === "\n") i += 1;
      row.push(field); field = "";
      rows.push(row); row = [];
      if (rows.length >= maxRows) return rows;
      continue;
    }
    field += char;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows;
}

function countOutsideQuotes(line: string, char: string): number {
  let count = 0;
  let quoted = false;
  for (const item of line) {
    if (item === "\"") quoted = !quoted;
    else if (!quoted && item === char) count += 1;
  }
  return count;
}

/** Строки таблицы как текст — то, что хранится в черновике и у источника (rawRowsFromCells). */
export type ExternalRawRows = string[][];

export function rawRowsFromCells(raw: readonly (readonly unknown[])[]): ExternalRawRows {
  return raw.map(row => row.map(cellText));
}

/**
 * Лист из строк: первая непустая строка — заголовки (пустые получают имя по
 * букве столбца), пустые строки и столбцы без заголовка и данных отбрасываются.
 * `headerRow: false` — заголовков нет, все строки данные, столбцы зовутся
 * «Столбец A/B/C…».
 */
export function sheetFromRows(raw: readonly (readonly unknown[])[], options: { headerRow?: boolean } = {}): ExternalSheet {
  const headerRow = options.headerRow ?? true;
  const rows = raw.map(row => row.map(cellText));
  const firstIndex = rows.findIndex(row => row.some(cell => cell.trim()));
  if (firstIndex === -1) return { headers: [], rows: [] };
  const headerIndex = headerRow ? firstIndex : firstIndex - 1;
  const width = Math.min(EXTERNAL_MAX_COLUMNS, Math.max(...rows.map(row => row.length)));
  const headers: string[] = [];
  const seen = new Map<string, number>();
  for (let index = 0; index < width; index += 1) {
    let header = (headerRow ? (rows[headerIndex][index] ?? "").trim().slice(0, EXTERNAL_HEADER_MAX) : "") || columnLetter(index);
    const dup = seen.get(header) ?? 0;
    seen.set(header, dup + 1);
    if (dup) header = `${header} (${dup + 1})`;
    headers.push(header);
  }
  const body = rows.slice(headerIndex + 1)
    .filter(row => row.some(cell => cell.trim()))
    .slice(0, EXTERNAL_MAX_ROWS)
    .map(row => headers.map((_, index) => (row[index] ?? "").trim()));
  // Столбцы без данных вовсе (пустые хвосты Excel) убираем.
  const keep = headers.map((_, index) => body.some(row => row[index]));
  return {
    headers: headers.filter((_, index) => keep[index]),
    rows: body.map(row => row.filter((_, index) => keep[index])),
  };
}

export function columnLetter(index: number): string {
  let out = "";
  let n = index;
  do { out = String.fromCharCode(65 + (n % 26)) + out; n = Math.floor(n / 26) - 1; } while (n >= 0);
  return `Столбец ${out}`;
}

// ── Google-таблица по ссылке ─────────────────────────────────────────────────

export type GoogleSheetRef = { spreadsheetId: string; gid: string | null };

/** Ссылка вида docs.google.com/spreadsheets/d/<id>/edit#gid=<gid> → id и лист. */
export function parseGoogleSheetUrl(raw: string): GoogleSheetRef | null {
  let url: URL;
  try { url = new URL(raw.trim()); } catch { return null; }
  if (url.hostname !== "docs.google.com") return null;
  const match = /^\/spreadsheets\/(?:u\/\d+\/)?d\/(?:e\/)?([a-zA-Z0-9_-]{4,})/.exec(url.pathname);
  if (!match) return null;
  const gid = /(?:^|[?&#])gid=(\d+)/.exec(url.search + url.hash)?.[1] ?? null;
  return { spreadsheetId: match[1], gid };
}

export function googleSheetCsvUrl(ref: GoogleSheetRef): string {
  return `https://docs.google.com/spreadsheets/d/${ref.spreadsheetId}/export?format=csv${ref.gid ? `&gid=${ref.gid}` : ""}`;
}

// ── Итоги у модели ───────────────────────────────────────────────────────────

/** Итог числовой колонки по размерам модели (или по всей таблице). */
export function aggregateExternalNumbers(values: ReadonlyArray<number | null | undefined>, aggregate: ExternalAggregate): number | null {
  const known = values.filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  if (!known.length) return null;
  switch (aggregate) {
    case "sum": return Math.round(known.reduce((a, b) => a + b, 0) * 100) / 100;
    case "avg": return Math.round(known.reduce((a, b) => a + b, 0) / known.length * 100) / 100;
    case "max": return Math.max(...known);
    case "min": return Math.min(...known);
    case "first": return known.every(value => value === known[0]) ? known[0] : null;
    case "none": return null;
  }
}

/**
 * Итог текста/ссылки: только когда значение есть у всех строк и оно одно —
 * иначе в строке модели (и в «Итого» по таблице) стояла бы заметка одного
 * размера как общая. Даты — по правилу среди известных: ближайший приход у
 * модели важен, даже если у части размеров даты нет.
 */
export function aggregateExternalTexts(values: ReadonlyArray<string | null | undefined>, type: ExternalColumnType, aggregate: ExternalAggregate): string | null {
  const known = values.filter((value): value is string => typeof value === "string" && value !== "");
  if (!known.length) return null;
  if (type === "date") {
    if (known.every(value => value === known[0])) return known[0];
    const sorted = [...known].sort();
    if (aggregate === "min" || aggregate === "first") return sorted[0];
    if (aggregate === "max") return sorted[sorted.length - 1];
    return null;
  }
  return known.length === values.length && known.every(value => value === known[0]) ? known[0] : null;
}

/** Число в ячейке: «1 234», «1 234,5»; единица — в шапке. */
export function formatExternalNumber(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return rounded.toLocaleString("ru-RU", { maximumFractionDigits: Number.isInteger(rounded) ? 0 : 2 }).replace(/ /g, " ");
}

/** «2026-09-21» → «21.09.2026». */
export function formatExternalDate(value: string): string {
  const [year, month, day] = value.split("-");
  return year && month && day ? `${day}.${month}.${year}` : value;
}

// ── Как часто перечитывать Google-таблицу (lib/external-sources-poller.ts) ──

/**
 * Поминутная проверка идёт только у таблиц, чьи колонки кто-то открыл на
 * странице за последние EXTERNAL_LIVE_MINUTES минут. Интервал зависит от
 * размера (скачать и разобрать 20 000 строк — секунда работы) и от того,
 * давно ли таблица менялась: спящую таблицу нет смысла дёргать каждую минуту.
 */
export const EXTERNAL_LIVE_MINUTES = 10;

export function externalPollBaseMinutes(rowsTotal: number): number {
  if (rowsTotal <= 1000) return 1;
  if (rowsTotal <= 5000) return 3;
  return 10;
}

/** Интервал проверки с учётом простоя: час без изменений — не чаще 5 минут, сутки — не чаще 30. */
export function externalPollMinutes(rowsTotal: number, dataChangedAt: string | null, now = Date.now()): number {
  const base = externalPollBaseMinutes(rowsTotal);
  const changed = dataChangedAt ? Date.parse(dataChangedAt) : Number.NaN;
  const idleMs = Number.isFinite(changed) ? now - changed : 0;
  if (idleMs > 24 * 60 * 60 * 1000) return Math.max(base, 30);
  if (idleMs > 60 * 60 * 1000) return Math.max(base, 5);
  return base;
}

function minutesWord(minutes: number): string {
  if (minutes === 1) return "каждую минуту";
  if (minutes < 5) return `каждые ${minutes} минуты`;
  return `каждые ${minutes} минут`;
}

/** Коротко для строки источника в редакторе: «проверка каждую минуту». */
export function externalRefreshShortLabel(source: Pick<ExternalSource, "kind" | "rowsTotal" | "dataChangedAt">): string {
  if (source.kind !== "google") return "обновляется новым файлом";
  return `проверка ${minutesWord(externalPollMinutes(source.rowsTotal, source.dataChangedAt))}`;
}

/** Подпись «как обновляется» для редактора, подсказки колонки и настроек. */
export function externalRefreshLabel(source: Pick<ExternalSource, "kind" | "rowsTotal" | "dataChangedAt">): string {
  if (source.kind !== "google") return "файл — обновляется загрузкой нового файла";
  const base = externalPollBaseMinutes(source.rowsTotal);
  const actual = externalPollMinutes(source.rowsTotal, source.dataChangedAt);
  const sleeping = actual > base ? `, сейчас ${minutesWord(actual)} — таблица давно не менялась` : "";
  return `Google-таблица проверяется ${minutesWord(base)}, пока страница с колонкой открыта${sleeping}; ночью — вместе с данными магазина`;
}
